import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import {
  Colors,
  Field,
  Link,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { addParamsToUrl } from '@zapier/url-utils';
import { urls, useSize } from '@zapier/identity-utils';
import { Button, atoms } from '@zapier/marketing-ui';
import { useTranslations } from 'next-intl';

import { useForgotPassword } from './useForgotPassword';
import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';

const messageStyle = css`
  ${Typography.Body1}
  color: ${Colors.GrayWarm9};
  text-align: center;
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.StatusSuccessStrongest};
`;

export function ForgotPasswordPage() {
  const router = useRouter();
  const size = useSize();
  const t = useTranslations('forgot-password');
  const { canSubmit, email, error, handleChangeEmail, handleSubmit, status } =
    useForgotPassword();

  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!error) {
      return;
    }

    // Submit disables the button and drops focus to <body>; refocus the field (SC 2.4.3).
    emailRef.current?.focus();
  }, [error]);

  function HelpLink(chunks: React.ReactNode) {
    return (
      <Link
        href={addParamsToUrl(urls.HELP_URL, {
          account: 'settings',
          from_url: router.asPath,
          settings: 'reset',
          topic: 'account',
        })}
      >
        {chunks}
      </Link>
    );
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={t('title')}
      titlePosition="right"
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        <p css={messageStyle}>{t('description')}</p>
        <p css={messageStyle}>{t.rich('forgot-email', { link: HelpLink })}</p>

        <Field
          error={error}
          // Keep: Field clamps to one line and hides the rest behind "More".
          helpTextLineClamp={3}
          label={t('email-label')}
          inputId="reset_password_email"
          isDisabled={status === 'submitting'}
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              // Keep: TextInput ignores camelCase ariaDescribedBy; pass the real attribute (SC 3.3.1).
              aria-describedby={inputProps.ariaDescribedBy}
              autoComplete="email"
              inputMode="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeEmail(e.target.value)
              }
              placeholder={t('email-placeholder')}
              ref={emailRef}
              type="email"
              value={email}
            />
          )}
        />
        <Button
          type="submit"
          variant="brand"
          fullWidth={true}
          disabled={!canSubmit || status !== 'initial'}
        >
          {t('submit-button')}
        </Button>
        {/* Always rendered, empty: a live region must exist before its message. */}
        <div
          className={status === 'success' ? undefined : atoms.srOnly}
          css={status === 'success' ? successStyle : undefined}
          role="status"
        >
          {status === 'success' ? t('success') : null}
        </div>
      </form>
    </AuthLayout>
  );
}
